import CUSTOMER_SUPPORT from 'constants/customerSupport';
import COUNTRY_CODE from 'constants/countryCode';
import { SESSION_STORAGE } from 'constants/session';
import Storage from 'utils/storageManager';
import PROFILE from 'constants/profile';
import VMasker from 'vanilla-masker';
import { isMasked } from './validation';
import { i18n } from './i18n';

const {
  PHONE_TYPE: {
    UNSUPPORTED_FORMATTING_MASK,
    GBO_DIGIT_REGEX_REPLACE_FROM,
    GBO_DIGIT_REPLACE_TO,
    GBO_MAX_PHONE_DIGITS_ALLOWED,
  },
} = CUSTOMER_SUPPORT;

export const isUnsupportedMask = (mask) =>
  !mask || typeof mask !== 'string' || mask.length === 0 || mask.toUpperCase() === UNSUPPORTED_FORMATTING_MASK;

// VMasker does use 9 as placeholder for numbers, not # as GBO does.
export const parseMaskToVMasker = (mask) => {
  if (isUnsupportedMask(mask)) {
    return null;
  }

  return mask.replace(GBO_DIGIT_REGEX_REPLACE_FROM, GBO_DIGIT_REPLACE_TO);
};

export const countMaskDigits = (mask) => {
  if (isUnsupportedMask(mask)) {
    return null;
  }
  return (mask.match(GBO_DIGIT_REGEX_REPLACE_FROM) || []).length;
};

export const parseCountryInfo = ({ country_content: content, country_name: countryName }) => {
  const {
    code,
    dialing_code: dialingCode,
    phone_number_format: phoneNumberFormat = UNSUPPORTED_FORMATTING_MASK,
  } = content;
  return {
    code,
    countryCode: code,
    dialingCode,
    phoneNumberFormat: parseMaskToVMasker(phoneNumberFormat),
    phoneNumberFormatDigitsCount: countMaskDigits(phoneNumberFormat),
    countryName,
  };
};

export const isValidCountryPhoneNumber = (country, phoneNumber) => {
  if (!country) {
    return false;
  }
  const { phoneNumberFormatDigitsCount, countryCode } = country;
  const shouldValidateCountry = countryCode === COUNTRY_CODE.CA || countryCode === COUNTRY_CODE.US;
  if (!shouldValidateCountry) {
    return phoneNumber;
  }

  const countryDoesntSupportsFormatting = isUnsupportedMask(country.phoneNumberFormat);
  const numberMatchMaskDigits = phoneNumber?.toString().length === phoneNumberFormatDigitsCount;

  return countryDoesntSupportsFormatting || numberMatchMaskDigits;
};

function extractDialingCode(phoneNumber, dialingCodes) {
  if (!phoneNumber || !dialingCodes) {
    return { dialingCode: null, localNumber: '' };
  }

  // regex to get a raw formatted phone number without any braces,
  const normalized = phoneNumber.replace(/[\s\-()]/g, '');

  // Split dialing codes that are starting with +
  const matchPlus = dialingCodes.find((code) => {
    const c = code.startsWith('+') ? code : `+${code}`;
    return normalized.startsWith(c);
  });
  if (matchPlus) {
    const normalizedCode = matchPlus.startsWith('+') ? matchPlus : `+${matchPlus}`;
    return {
      dialingCode: normalizedCode,
      localNumber: normalized.slice(normalizedCode.length),
    };
  }

  // if No matches found, returning default values
  return {
    dialingCode: null,
    localNumber: normalized,
  };
}

/**
 * @name trimDialingCodeIfExists

 * @returns phonenumber trimming dialingcode if exists
 */
export const trimDialingCodeIfExists = (country, phone) => {
  if (!phone?.length || !country) {
    return phone;
  }

  if (
    !!phone &&
    phone !== country.dialingCode &&
    !isNaN(isValidCountryPhoneNumber(country, phone)) &&
    (!phone?.startsWith('+') || !phone?.includes(' '))
  ) {
    if (!phone?.startsWith('+')) {
      // Sanitize the dialing code to digits only
      const dialingCode = String(country.dialingCode || '').replace(/\D+/g, '');
      if (!dialingCode) return phone?.replace(/\D+/g, ''); // phone  has no dialingCode
      const regularExp = new RegExp(`^\\s*\\+?${dialingCode}\\s*`);
      // Remove the leading dialingCode using regex ,if exists
      const phoneWithoutDialingCode = phone?.replace(regularExp, '');
      // formatting the phonenumber to inlcude only digits , without any numeric values
      return phoneWithoutDialingCode?.replace(/\D+/g, '');
    }

    const phoneObj = extractDialingCode(phone, country.dialingCodes);
    return phoneObj.localNumber;
  }
  return phone
    ?.replace(country.dialingCode || '', '')
    .replace(/[^\d.]/g, '')
    .replace(/\./g, '')
    .substring(0, GBO_MAX_PHONE_DIGITS_ALLOWED);
};

export const getFormattedValue = (phoneNumber, country) => {
  if (!country?.dialingCode || isMasked(phoneNumber)) {
    return phoneNumber;
  }

  const value = trimDialingCodeIfExists(country, phoneNumber);
  return value ? value?.replace(`${country.dialingCode} `, '').replace(`${country.dialingCode}`, '') : '';
};

/**
 * Gets the raw phone number
 * @param {formatCountryPhoneNumber} country - The country info
 * @param {string} phoneNumber - The phone number
 * @returns {string} - The raw phone number
 */
export const getRawPhoneNumber = (country, phoneNumber) => {
  let formattedNumber = phoneNumber || '';
  if (!country) return formattedNumber;

  const { phoneNumberFormatDigitsCount = null } = country;
  const hasFormattingMask = phoneNumberFormatDigitsCount;

  formattedNumber = !isValidCountryPhoneNumber(country, formattedNumber)
    ? trimDialingCodeIfExists(country, formattedNumber)
    : formattedNumber;

  const onlyDigitsNumber = formattedNumber
    ?.replace(country.dialingCode || '', '')
    .replace(/[^\d.]/g, '')
    .replace(/\./g, '')
    .substr(0, GBO_MAX_PHONE_DIGITS_ALLOWED);

  if (hasFormattingMask) {
    return onlyDigitsNumber?.substr(0, phoneNumberFormatDigitsCount);
  }

  return onlyDigitsNumber;
};

export const selectedCountryDetails = {
  get: () => Storage.SessionStorage.get(SESSION_STORAGE.COUNTRY_SELECTED, true),
  remove: () => Storage.SessionStorage.remove(SESSION_STORAGE.COUNTRY_SELECTED),
};

export const isDialingCode = (phoneNumber) => {
  const parsedCountryObject = selectedCountryDetails.get();
  const { dialingCodes = [] } = parsedCountryObject || {};
  return dialingCodes?.some((item) => item === phoneNumber?.trimEnd());
};

export const getMaskedPhoneNumber = (phoneNumber) => phoneNumber?.replace(/.(?=.{4})/g, '*');

export const getMaskedEmail = (emailId) => emailId?.replace(/(\w{1})(.*)(\w{1})@(.*)/, '$1******$3@$4');

export const areSamePhoneNumberFormat = (num1, num2) => {
  // Remove all non-numeric characters
  const sanitize = (str) => str && str?.replace(/\D/g, '');
  return sanitize(num1) === sanitize(num2);
};

/**
 * Returns the complete phone object from a given phones array with the given priority default to 1 (primary)
 *
 * @param {array} phones Phones array from user's profile
 * @param {number} priority Priority value (1 primary / 2 alternate)
 * @returns {object} - Complete phone object
 */
export const getPhoneByPriority = (phones, priority = 1) => phones?.find((item) => item.priority === priority) || false;

/**
 * Format a passed in array of phone data and strip out special characters
 * @param phoneData
 * @returns {Array}
 */
export const formatPhoneData = (phoneData) =>
  Object.keys(phoneData)
    .filter((key) => phoneData[key].phone_number)
    .map((key, i) => {
      const item = phoneData[key];
      item.priority = i + 1;
      if (!isMasked(item.phone_number)) {
        item.phone_number = item.phone_number.replace(/\D/g, '');
      }
      if (!item.phone_type) {
        item.phone_type = PROFILE.PHONE_TYPE_HOME;
      }
      return item;
    });

/**
 * Unformats a USA/CA phone number.
 * @param {string} formattedNumber - The formatted phone number.
 * @returns {string} - The unformatted phone number.
 */
export const unformatPhoneNumber = (formattedNumber) => {
  const unformattedNumber = formattedNumber?.replace(/[^\d]/g, '');
  return unformattedNumber;
};

const getFormattedPhoneNumber = (phone) => {
  if (phone?.startsWith('+') && !phone.includes(' ')) {
    const countryInfo = selectedCountryDetails.get();
    return trimDialingCodeIfExists(countryInfo, phone);
  }
  return phone;
};

/**
 * Formats the country phone number
 * @param {formatCountryPhoneNumber} country - The country info
 * @param {string} phoneNumber - The phone number
 * @returns {string} - The formatted phone number
 */
export const formatCountryPhoneNumber = (country, phoneNumber) => {
  if (!country) return phoneNumber;
  const { phoneNumberFormat } = country;

  if (!phoneNumberFormat) {
    return phoneNumber;
  }

  const rawPhoneNumber = getRawPhoneNumber(country, phoneNumber);
  return VMasker.toPattern(rawPhoneNumber, phoneNumberFormat);
};

/**
 * Format a passed in array of phone data and strip out special characters
 * @param phoneData
 * @returns {Array}
 */
export const formatPhoneDataBasedOnCountryCode = (phoneData) =>
  Object.keys(phoneData)
    .filter((key) => phoneData[key].phone_number)
    .map((key, i) => {
      const item = phoneData[key];
      item.priority = i + 1;
      if (!isMasked(item.phone_number) && isDialingCode(item.phone_number?.trimEnd())) {
        return null;
      }
      if (!isMasked(item.phone_number)) {
        const formatPhone =
          item.phone_number?.split(' ')?.length > 1
            ? item.phone_number?.split(' ').slice(1).join(' ')
            : getFormattedPhoneNumber(item.phone_number);

        item.phone_number = unformatPhoneNumber(formatPhone);
      }
      if (!item.phone_type) {
        item.phone_type = PROFILE.PHONE_TYPE_HOME;
      }

      const selectedCountry = selectedCountryDetails.get();
      // setting default country code based on the domain if the country details are not present
      // below condition is to send updated country code, if phone number is changed
      if (
        Object.hasOwn(item, 'phone_number') &&
        item.phone_number.length > 0 &&
        !isMasked(item.phone_number) &&
        (!Object.hasOwn(item, 'country_code') ||
          (selectedCountry?.countryCode && item.country_code !== selectedCountry.countryCode))
      ) {
        item.country_code = selectedCountry.countryCode;
        item.country_name = selectedCountry.countryName;
      }

      return item;
    });

export const getMaskedString = (inputString) => inputString?.replace(/./g, '•');

export const phone = (value) => (/^[+\d\s\-().•]+$/.test(value) ? null : i18n('review_page_phone_validation'));

export const checkPhoneCode = (value) => {
  const parsedCountryObject = selectedCountryDetails.get();
  // wait for parsed country details to be available to avoid error (e.g. deeplink/autofill scenarios)
  if (!parsedCountryObject) return null;

  const rawPhoneNumber = isMasked(value) ? value : getRawPhoneNumber(parsedCountryObject, value);
  const hasValidCountryPhoneNumber = isValidCountryPhoneNumber(parsedCountryObject, rawPhoneNumber);
  const phoneNumbeFieldRequiredError = rawPhoneNumber === '' || isDialingCode(value);

  if (!isMasked(rawPhoneNumber) && (phoneNumbeFieldRequiredError || !hasValidCountryPhoneNumber)) {
    if (phoneNumbeFieldRequiredError) {
      return i18n('field_required_dont_forget_to_enter', [i18n('common_phone').toLowerCase()]);
    }

    return i18n('field_must_be_valid_phone_number');
  }
  return null;
};

/**
 * This function checks if phone number validation
 * is met (phone number is valid) in order to show an error on the field or not.
 * This is different from showing a 'required' error on the password field.
 */
export const altPhoneNumberValidation = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim() === '') return null; // If the phone number is empty, no validation error
  const parsedCountryObject = selectedCountryDetails.get();
  // wait for parsed country details to be available to avoid error (e.g. deeplink/autofill scenarios)
  if (!parsedCountryObject) return null;

  const rawPhoneNumber = isMasked(phoneNumber) ? phoneNumber : getRawPhoneNumber(parsedCountryObject, phoneNumber);
  if (
    phoneNumber.length > 0 &&
    !isDialingCode(phoneNumber) &&
    !isMasked(rawPhoneNumber) &&
    rawPhoneNumber.length > 0 &&
    rawPhoneNumber !== '' &&
    checkPhoneCode(rawPhoneNumber)
  ) {
    return i18n('field_required_dont_forget_to_enter', [i18n('common_phone').toLowerCase()]);
  }

  return null;
};

/**
 * Parses the countries list and returns unique list of dialing codes for formatting country based phonenumbers
 * @returns {getUniqueDialCodes}
 * }} - Helper object to generate unique set of dialing Codes
 */
const getUniqueDialCodes = (countryList) => {
  const unique = new Set();
  countryList?.forEach((country) => {
    const dialingCode = country?.dialingCode || country.country_content.dialing_code;
    if (dialingCode) {
      unique.add(dialingCode.startsWith('+') ? dialingCode : `+${dialingCode}`);
    }
  });

  return Array.from(unique);
};

/**
 * Parses the countries list and returns a helper object to handle phone number formatting
 * @param {import('Typesjs/locationProperties').CountryGMAType[]} [countries] - List of countries
 * @returns {ParsedCountries}
 * }} - Helper object to handle phone number formatting
 */
export const parseCountries = (countries) => {
  if (!countries) {
    return false;
  }
  const parsedCountries = countries.map(parseCountryInfo);

  return {
    getCountryByCode: (countryCode) => {
      const selectedCountryIndex = parsedCountries.findIndex(({ code }) => code === countryCode);
      const selectedCountry = parsedCountries[selectedCountryIndex];
      if (!selectedCountry) return null;

      return {
        ...selectedCountry,
        formatPhoneNumber: (phoneNumber) => formatCountryPhoneNumber(selectedCountry, phoneNumber),
        getRawPhoneNumber: (phoneNumber) => getRawPhoneNumber(selectedCountry, phoneNumber),
        isValidPhoneNumber: (phoneNumber) => isValidCountryPhoneNumber(selectedCountry, phoneNumber),
        dialingCodes: getUniqueDialCodes(parsedCountries),
      };
    },
    countries: parsedCountries,
  };
};
